import { _js } from '@ifixit/utils/localize';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
   AlertDialogTrigger,
} from '@ifixit/ui/components/alert-dialog';
import { Button, ButtonStyles } from '@ifixit/ui/components/button';
import { cn } from '@ifixit/ui/lib/utils';
import type { ComponentProps } from 'react';

/**
 * The editors' primary (brand-filled) action button. ButtonStyles has no
 * filled variant yet (DESIGN.md § Gaps sanctions this one use), so the
 * override lives here once instead of being pasted per save button.
 *
 * Deliberately NOT built on the `button-primary` utility: that utility's
 * disabled state is a gray-300 fill, which reads as "unavailable". A valid
 * form with nothing to save has to stay brand-filled and merely fade
 * (DESIGN.md's Idle-Is-Not-Broken Rule), which is what `disabled:opacity-50`
 * on the utilities layer gives us.
 *
 * `!h-9` because ButtonStyles' base is `h-7 md:h-9`: it would render 28px
 * below md, next to the 36px `button-md` controls it shares an action bar
 * with. Class order can't settle two same-property utilities, so this needs
 * the important flag rather than a plain `h-9`.
 *
 * `isPending` disables and sets `aria-busy` together: disabling the pressed
 * button blurs it mid-request, so the swapped label alone is silent. Pair it
 * with a WriteStatusAnnouncer for the spoken status, which has to sit outside
 * any region the page makes `inert` while writing.
 */
export function PrimaryButton({
   className,
   isPending = false,
   disabled = false,
   ...props
}: ComponentProps<typeof Button> & { isPending?: boolean }) {
   return (
      <Button
         type="button"
         aria-busy={isPending || undefined}
         disabled={disabled || isPending}
         className={cn(
            ButtonStyles({ variant: 'default' }),
            '!h-9 border-transparent bg-brand-500 text-white hover:border-transparent hover:bg-brand-600 hover:text-white disabled:opacity-50',
            className
         )}
         {...props}
      />
   );
}

/**
 * A button that confirms through an AlertDialog before firing — for
 * consequential but non-destructive actions (approving a translation
 * stamps a review nothing can unstamp). The dialog owns focus and
 * Escape handling, which hand-rolled inline confirms kept getting wrong.
 */
export function ConfirmActionButton({
   label,
   pendingLabel,
   title,
   description,
   confirmLabel,
   isPending,
   disabled = false,
   triggerClassName,
   triggerTestId,
   triggerKeyShortcuts,
   onConfirm,
}: {
   label: string;
   pendingLabel: string;
   title: string;
   description: string;
   confirmLabel: string;
   isPending: boolean;
   disabled?: boolean;
   /** Styles the trigger to fit its host (save bar vs banner). */
   triggerClassName?: string;
   /** Lets a caller's tests treat this trigger as the same button as its
    *  unconfirmed sibling, which is what it is to the person clicking. */
   triggerTestId?: string;
   /** The key that presses this trigger, for callers whose shortcut reaches
    *  both it and its unconfirmed sibling. Announced the same either way, so
    *  a screen reader does not lose the key on the cards that confirm. */
   triggerKeyShortcuts?: string;
   onConfirm: () => void;
}) {
   return (
      <AlertDialog>
         <AlertDialogTrigger asChild>
            <button
               type="button"
               data-testid={triggerTestId}
               aria-keyshortcuts={triggerKeyShortcuts}
               className={triggerClassName ?? 'button-md button-outline'}
               aria-busy={isPending || undefined}
               disabled={isPending || disabled}
            >
               {isPending ? pendingLabel : label}
            </button>
         </AlertDialogTrigger>
         <AlertDialogContent>
            <AlertDialogHeader>
               <AlertDialogTitle>{title}</AlertDialogTitle>
               <AlertDialogDescription>{description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
               <AlertDialogCancel>{_js('Cancel')}</AlertDialogCancel>
               <AlertDialogAction onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
}

/**
 * A red outline button that confirms through an AlertDialog before firing.
 * Used for the destructive editor actions (delete page, delete step).
 */
export function DangerConfirmButton({
   label,
   pendingLabel,
   title,
   description,
   isPending,
   disabled = false,
   disabledReason,
   onConfirm,
}: {
   label: string;
   pendingLabel: string;
   title: string;
   description: string;
   isPending: boolean;
   /** Blocks the action without the pending label, e.g. while a conflicting
    *  write (a save) is running. */
   disabled?: boolean;
   /** Why the button is disabled, surfaced as the tooltip a bare disabled
    *  state doesn't explain. */
   disabledReason?: string;
   onConfirm: () => void;
}) {
   return (
      <AlertDialog>
         <AlertDialogTrigger asChild>
            <Button
               type="button"
               // button-md/button-outline rather than ButtonStyles (DESIGN.md
               // § Don't): same 36px height as the rest of the action bar at
               // every width, and one source for the focus ring. The red
               // overrides sit on the utilities layer, so they beat
               // button-outline's component-layer border and text color
               // whatever the class order.
               className={cn(
                  'button-md button-outline',
                  'border-red-300 text-red-600 hover:border-red-400 hover:text-red-700 disabled:opacity-50'
               )}
               aria-busy={isPending || undefined}
               disabled={isPending || disabled}
               title={disabled && !isPending ? disabledReason : undefined}
            >
               {isPending ? pendingLabel : label}
            </Button>
         </AlertDialogTrigger>
         <AlertDialogContent>
            <AlertDialogHeader>
               <AlertDialogTitle>{title}</AlertDialogTitle>
               <AlertDialogDescription>{description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
               <AlertDialogCancel>{_js('Cancel')}</AlertDialogCancel>
               {/* Destructive confirm matching the red trigger. AlertDialogAction
                   is brand-primary by default; these utilities-layer classes
                   override its component-layer background regardless of class
                   order (button-destructive wouldn't reliably win the cascade). */}
               <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/80 focus-visible:ring-destructive/20"
                  onClick={onConfirm}
               >
                  {_js('Delete')}
               </AlertDialogAction>
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
}
